import {
    max,
} from 'lodash-es';
import {
    useMemo,
} from 'react';

import {TColumnDropdownSort, IColumnSortData} from '../../table-column-menu-types';
import {adaptSortDataForRequest} from './use-table-column-sort-utils';

const DEFAULT_SORT_DATA = {
    order: undefined,
    multiple: undefined,
} as const;

export type TUseTableColumnSortArgs = {
    tableColumnSortData: IColumnSortData;
    setTableColumnSortData: (data: IColumnSortData) => void;
    isSingleColumnSort?: boolean;
}

export const useTableColumnSort = (args: TUseTableColumnSortArgs) => {
    const {
        tableColumnSortData,
        setTableColumnSortData,
        isSingleColumnSort = false,
    } = args;

    const resetAllFiltersSortData = () => {
        setTableColumnSortData({
            sort: {},
        });
    };

    const createSortDataGetter = (columnKey: string) => () => tableColumnSortData.sort
        ?.[columnKey] ?? DEFAULT_SORT_DATA;

    const createSortDataSetter = (columnKey: string) => (newSortData:
        Partial<Exclude<TColumnDropdownSort, undefined>>) => {
        const prevSortData = tableColumnSortData.sort;
        const prevColumnSortData = prevSortData?.[columnKey] ?? DEFAULT_SORT_DATA;

        const sortEntriesFiltered = Object.entries(tableColumnSortData.sort ?? {})
            .filter(([, value]) => !!value?.order);

        const multiple = !sortEntriesFiltered.length ? 1 : (max(
            sortEntriesFiltered.filter(([key]) => key !== columnKey)
                .map(([, d]) => d?.multiple ?? 0),
        ) ?? 0) + 1;

        setTableColumnSortData({
            ...tableColumnSortData,
            sort: {
                ...(isSingleColumnSort ? {} : prevSortData),
                [columnKey]: {
                    ...prevColumnSortData,
                    ...newSortData,
                    multiple,
                },
            },
        });
    };

    const tableColumnSortDataAdaptedForRequest = useMemo(() => adaptSortDataForRequest(
        tableColumnSortData.sort,
    ), [tableColumnSortData]);

    return {
        tableColumnSortData,
        tableColumnSortDataAdaptedForRequest,
        createSortDataGetter,
        createSortDataSetter,
        resetAllFiltersSortData,
    };
};

export type TSetSortDataFunction = ReturnType<ReturnType<typeof useTableColumnSort>['createSortDataSetter']>;
export type TGetSortDataFunction = ReturnType<ReturnType<typeof useTableColumnSort>['createSortDataGetter']>;
export type TSortDataAdaptedForRequest = ReturnType<
     typeof useTableColumnSort
>['tableColumnSortDataAdaptedForRequest'];
